import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronUp, Search, Type, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

// Fonts available in OpenSCAD (bundled via fontconfig: Liberation, Noto, DejaVu, etc.)
// Format: { name: display name, scad: exact OpenSCAD font string, style: CSS fallback }
export interface ScadFont {
  name: string;
  /** Exact string to pass to OpenSCAD font="..." */
  scad: string;
  /** CSS font-family for the preview. Uses web-safe / Google Fonts lookalikes. */
  css: string;
  /** Category tag for grouping */
  category: 'serif' | 'sans-serif' | 'mono' | 'display' | 'handwriting';
}

export const OPENSCAD_FONTS: ScadFont[] = [
  // Liberation (default OpenSCAD fonts)
  {
    name: 'Liberation Sans',
    scad: 'Liberation Sans',
    css: 'Arial, Helvetica, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Liberation Sans Bold',
    scad: 'Liberation Sans:style=Bold',
    css: '"Arial Black", Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Liberation Sans Italic',
    scad: 'Liberation Sans:style=Italic',
    css: 'Arial, Helvetica, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Liberation Serif',
    scad: 'Liberation Serif',
    css: 'Georgia, "Times New Roman", serif',
    category: 'serif',
  },
  {
    name: 'Liberation Serif Bold',
    scad: 'Liberation Serif:style=Bold',
    css: 'Georgia, "Times New Roman", serif',
    category: 'serif',
  },
  {
    name: 'Liberation Mono',
    scad: 'Liberation Mono',
    css: '"Courier New", Courier, monospace',
    category: 'mono',
  },
  {
    name: 'Liberation Mono Bold',
    scad: 'Liberation Mono:style=Bold',
    css: '"Courier New", Courier, monospace',
    category: 'mono',
  },
  // DejaVu (commonly bundled with OpenSCAD)
  {
    name: 'DejaVu Sans',
    scad: 'DejaVu Sans',
    css: 'Verdana, Geneva, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'DejaVu Sans Bold',
    scad: 'DejaVu Sans:style=Bold',
    css: 'Verdana, Geneva, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'DejaVu Sans Condensed',
    scad: 'DejaVu Sans Condensed',
    css: 'Verdana, Geneva, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'DejaVu Serif',
    scad: 'DejaVu Serif',
    css: 'Georgia, "Times New Roman", serif',
    category: 'serif',
  },
  {
    name: 'DejaVu Serif Bold',
    scad: 'DejaVu Serif:style=Bold',
    css: 'Georgia, "Times New Roman", serif',
    category: 'serif',
  },
  {
    name: 'DejaVu Serif Condensed',
    scad: 'DejaVu Serif Condensed',
    css: 'Georgia, "Times New Roman", serif',
    category: 'serif',
  },
  {
    name: 'DejaVu Sans Mono',
    scad: 'DejaVu Sans Mono',
    css: '"Lucida Console", Monaco, monospace',
    category: 'mono',
  },
  {
    name: 'DejaVu Sans Mono Bold',
    scad: 'DejaVu Sans Mono:style=Bold',
    css: '"Lucida Console", Monaco, monospace',
    category: 'mono',
  },
  // Noto (bundled in many OpenSCAD distros / Linux)
  {
    name: 'Noto Sans',
    scad: 'Noto Sans',
    css: '"Noto Sans", Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Noto Sans Bold',
    scad: 'Noto Sans:style=Bold',
    css: '"Noto Sans", Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Noto Serif',
    scad: 'Noto Serif',
    css: '"Noto Serif", Georgia, serif',
    category: 'serif',
  },
  {
    name: 'Noto Serif Bold',
    scad: 'Noto Serif:style=Bold',
    css: '"Noto Serif", Georgia, serif',
    category: 'serif',
  },
  {
    name: 'Noto Sans Mono',
    scad: 'Noto Sans Mono',
    css: '"Noto Sans Mono", "Courier New", monospace',
    category: 'mono',
  },
  // Overpass (bundled with some OpenSCAD versions)
  {
    name: 'Overpass',
    scad: 'Overpass',
    css: 'Futura, "Century Gothic", sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Overpass Bold',
    scad: 'Overpass:style=Bold',
    css: 'Futura, "Century Gothic", sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Overpass Mono',
    scad: 'Overpass Mono',
    css: '"Courier New", monospace',
    category: 'mono',
  },
  // Lato (macOS bundled / common system font)
  {
    name: 'Lato',
    scad: 'Lato',
    css: 'Lato, "Helvetica Neue", sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Lato Bold',
    scad: 'Lato:style=Bold',
    css: 'Lato, "Helvetica Neue", sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Lato Light',
    scad: 'Lato:style=Light',
    css: 'Lato, "Helvetica Neue", sans-serif',
    category: 'sans-serif',
  },
  // Ubuntu (bundled in Ubuntu Linux distros; often on macOS/Windows too)
  {
    name: 'Ubuntu',
    scad: 'Ubuntu',
    css: 'Ubuntu, Calibri, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Ubuntu Bold',
    scad: 'Ubuntu:style=Bold',
    css: 'Ubuntu, Calibri, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Ubuntu Mono',
    scad: 'Ubuntu Mono',
    css: '"Ubuntu Mono", "Courier New", monospace',
    category: 'mono',
  },
  // Open Sans (very commonly installed)
  {
    name: 'Open Sans',
    scad: 'Open Sans',
    css: '"Open Sans", Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Open Sans Bold',
    scad: 'Open Sans:style=Bold',
    css: '"Open Sans", Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Open Sans Light',
    scad: 'Open Sans:style=Light',
    css: '"Open Sans", Arial, sans-serif',
    category: 'sans-serif',
  },
  // Roboto (very common on Android / Google systems)
  {
    name: 'Roboto',
    scad: 'Roboto',
    css: 'Roboto, Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Roboto Bold',
    scad: 'Roboto:style=Bold',
    css: 'Roboto, Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Roboto Light',
    scad: 'Roboto:style=Light',
    css: 'Roboto, Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Roboto Condensed',
    scad: 'Roboto Condensed',
    css: '"Roboto Condensed", Arial Narrow, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Roboto Mono',
    scad: 'Roboto Mono',
    css: '"Roboto Mono", "Courier New", monospace',
    category: 'mono',
  },
  // Source fonts (Adobe, widely distributed)
  {
    name: 'Source Code Pro',
    scad: 'Source Code Pro',
    css: '"Source Code Pro", "Courier New", monospace',
    category: 'mono',
  },
  {
    name: 'Source Code Pro Bold',
    scad: 'Source Code Pro:style=Bold',
    css: '"Source Code Pro", "Courier New", monospace',
    category: 'mono',
  },
  {
    name: 'Source Sans 3',
    scad: 'Source Sans 3',
    css: '"Source Sans 3", "Source Sans Pro", Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Source Serif 4',
    scad: 'Source Serif 4',
    css: '"Source Serif 4", "Source Serif Pro", Georgia, serif',
    category: 'serif',
  },
  // Droid (older Android / Java, still widely available)
  {
    name: 'Droid Sans',
    scad: 'Droid Sans',
    css: '"Droid Sans", Arial, sans-serif',
    category: 'sans-serif',
  },
  {
    name: 'Droid Serif',
    scad: 'Droid Serif',
    css: '"Droid Serif", Georgia, serif',
    category: 'serif',
  },
  {
    name: 'Droid Sans Mono',
    scad: 'Droid Sans Mono',
    css: '"Droid Sans Mono", "Courier New", monospace',
    category: 'mono',
  },
  // Inconsolata (popular programming font)
  {
    name: 'Inconsolata',
    scad: 'Inconsolata',
    css: 'Inconsolata, "Courier New", monospace',
    category: 'mono',
  },
  // Courier Prime
  {
    name: 'Courier Prime',
    scad: 'Courier Prime',
    css: '"Courier Prime", "Courier New", monospace',
    category: 'mono',
  },
  // Note: proprietary system fonts (Impact, Times New Roman, Palatino, etc.)
  // are not available in the WASM environment and have been removed.
  // Oswald (condensed display)
  {
    name: 'Oswald',
    scad: 'Oswald',
    css: 'Oswald, Impact, sans-serif',
    category: 'display',
  },
  // Orbitron (sci-fi display)
  {
    name: 'Orbitron',
    scad: 'Orbitron',
    css: 'Orbitron, "Courier New", monospace',
    category: 'display',
  },
  // Pacifico (handwriting/display)
  {
    name: 'Pacifico',
    scad: 'Pacifico',
    css: 'Pacifico, cursive',
    category: 'handwriting',
  },
  // Dancing Script
  {
    name: 'Dancing Script',
    scad: 'Dancing Script',
    css: '"Dancing Script", cursive',
    category: 'handwriting',
  },
  // Satisfy
  {
    name: 'Satisfy',
    scad: 'Satisfy',
    css: 'Satisfy, cursive',
    category: 'handwriting',
  },
];

export const FONT_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'sans-serif', label: 'Sans' },
  { id: 'serif', label: 'Serif' },
  { id: 'mono', label: 'Mono' },
  { id: 'display', label: 'Display' },
  { id: 'handwriting', label: 'Script' },
] as const;

function getFontByScad(scad: string): ScadFont | undefined {
  return OPENSCAD_FONTS.find((f) => f.scad === scad);
}

/** Derive CSS font-family string for the preview of a given scad font string */
function getCssForFont(scad: string): string {
  const font = getFontByScad(scad);
  if (font) return font.css;
  // Fallback: strip style suffix and use the base name
  const base = scad.split(':')[0];
  return `"${base}", Arial, sans-serif`;
}

function getDisplayName(scad: string): string {
  const font = getFontByScad(scad);
  if (font) return font.name;
  return scad;
}

/** Style modifier to apply to the preview text based on the SCAD font string */
function getFontStyle(scad: string): React.CSSProperties {
  const style: React.CSSProperties = {};
  if (scad.includes('Bold')) style.fontWeight = 'bold';
  if (scad.includes('Italic') || scad.includes('Oblique'))
    style.fontStyle = 'italic';
  if (scad.includes('Light') || scad.includes('Thin')) style.fontWeight = '300';
  if (
    scad.includes('Condensed') ||
    scad.includes('Narrow') ||
    scad.includes('Compressed')
  )
    style.fontStretch = 'condensed';
  return style;
}

interface FontInputProps {
  value: string;
  onChange: (font: string) => void;
  /** Sample text for the preview */
  previewText?: string;
}

export function FontInput({
  value,
  onChange,
  previewText = 'The quick brown fox',
}: FontInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [hoveredFont, setHoveredFont] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(
    undefined,
  );
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth);
    }
    if (isOpen) {
      // Auto-focus search on open
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
      setHoveredFont(null);
    }
  }, [isOpen]);

  const filteredFonts = useMemo(() => {
    return OPENSCAD_FONTS.filter((f) => {
      const matchesSearch =
        search === '' ||
        f.name.toLowerCase().includes(search.toLowerCase()) ||
        f.scad.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = category === 'all' || f.category === category;
      return matchesSearch && matchesCategory;
    });
  }, [search, category]);

  // The font currently shown in the large preview (hovered or selected)
  const previewFont = hoveredFont ?? value;
  const previewCss = getCssForFont(previewFont);
  const previewStyle = getFontStyle(previewFont);
  const displayName = getDisplayName(value);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          aria-label="Toggle font picker"
          className="relative flex h-8 w-full items-center justify-between overflow-hidden rounded-md bg-muted pl-3 pr-0 text-sm transition-colors duration-200 ease-out focus:outline-none [@media(hover:hover)]:hover:bg-muted/80"
        >
          <div className="flex min-w-0 items-center gap-2">
            <Type className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span
              className="truncate text-xs text-foreground"
              style={{ fontFamily: previewCss, ...getFontStyle(value) }}
            >
              {displayName}
            </span>
          </div>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
            <ChevronUp
              className={`h-4 w-4 text-foreground transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex flex-col gap-0 rounded-md border-border bg-background p-0 shadow-md"
        style={{ width: Math.max(popoverWidth ?? 0, 300), minWidth: 300 }}
      >
        {/* Live preview panel */}
        <div className="flex h-[64px] items-center justify-center overflow-hidden border-b border-border px-4">
          <p
            className="w-full overflow-hidden whitespace-nowrap text-center text-xl leading-none text-foreground"
            style={{
              fontFamily: previewCss,
              ...previewStyle,
            }}
          >
            {previewText}
          </p>
        </div>

        {/* Search + category filter */}
        <div className="flex flex-col gap-2 border-b border-border px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search fonts…"
              className="h-7 pl-7 pr-7 text-xs"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors [@media(hover:hover)]:hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex gap-1">
            {FONT_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`rounded px-2 py-0.5 text-[11px] transition-colors duration-150 ${
                  category === cat.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground [@media(hover:hover)]:hover:bg-muted [@media(hover:hover)]:hover:text-foreground'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Font list */}
        <ScrollArea className="h-[240px]">
          {filteredFonts.length === 0 ? (
            <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
              No fonts match "{search}"
            </div>
          ) : (
            <div className="flex flex-col py-1">
              {filteredFonts.map((font) => {
                const isSelected = font.scad === value;
                return (
                  <button
                    key={font.scad}
                    onClick={() => {
                      onChange(font.scad);
                      setIsOpen(false);
                    }}
                    onMouseEnter={() => setHoveredFont(font.scad)}
                    onMouseLeave={() => setHoveredFont(null)}
                    className={`flex items-center justify-between px-3 py-2 text-left transition-colors duration-100 ${
                      isSelected
                        ? 'bg-primary/10 text-foreground'
                        : 'text-foreground [@media(hover:hover)]:hover:bg-muted'
                    }`}
                  >
                    {/* Font name rendered in the font's own style */}
                    <span
                      className="text-sm leading-none"
                      style={{
                        fontFamily: font.css,
                        ...getFontStyle(font.scad),
                      }}
                    >
                      {font.name}
                    </span>
                    {isSelected && (
                      <span className="ml-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
